import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Phone, User, Lock, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { saveToken } from "../../utils/tokenStorage";
import { validateCustomerName } from "../../utils/nameValidator";
import { guardarPulseira } from "@/features/cliente-sessao";

const API = '/api';

// Mesmo número do suporte do menu (features/suporte-menu/SuporteMenu.jsx).
const WHATSAPP_SUPORTE_PAGAMENTO =
  "https://wa.me/553431970131?text=Meu%20teste%20gr%C3%A1tis%20acabou%20e%20quero%20pagar%20a%20primeira%20mensalidade.";

const formatPhoneDisplay = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
};

const ensureNinthDigit = (phone) => {
  const numbers = phone.replace(/\D/g, '');
  if (numbers.length === 10) return numbers.slice(0, 2) + '9' + numbers.slice(2);
  return numbers;
};

// A logo chega de três jeitos, conforme a época do upload: endereço completo,
// já com /api, ou só /uploads/…. Somar /api ao que já tinha dava /api/api/… e
// a janela abria sem a logo (achado 404).
const enderecoDaLogo = (url) => (url.startsWith("http") || url.startsWith("/api/") ? url : `/api${url}`);

export default function LoginModal({ isOpen, onClose, onLoginSuccess, logoUrl }) {
  const [mode, setMode] = useState("phone"); // "phone" | "password"
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [phoneChecked, setPhoneChecked] = useState(false);
  const [isNewClient, setIsNewClient] = useState(false);
  const [loginInfo, setLoginInfo] = useState(null);
  const [avisoTeste, setAvisoTeste] = useState(""); // mensagem do 403 "teste_vencido"
  const nameRef = useRef(null);
  const checkTimer = useRef(null);

  const handleClose = () => {
    setMode("phone"); setPhone(""); setName(""); setPassword("");
    setLoading(false); setCheckingPhone(false); setPhoneChecked(false);
    setIsNewClient(false); setLoginInfo(null); setAvisoTeste("");
    onClose();
  };

  // Detectar se é telefone ou username
  const phoneDigits = phone.replace(/\D/g, '');
  const isPhoneInput = /^\(?\d/.test(phone.trim()) && phoneDigits.length >= 8;
  const isUsernameInput = phone.trim().length >= 3 && /[a-zA-Z]/.test(phone);

  // Auto-check quando digita telefone completo OU username
  useEffect(() => {
    if (isPhoneInput && phoneDigits.length >= 10) {
      if (checkTimer.current) clearTimeout(checkTimer.current);
      checkTimer.current = setTimeout(() => checkIdentifier(phoneDigits), 400);
    } else if (isUsernameInput) {
      if (checkTimer.current) clearTimeout(checkTimer.current);
      checkTimer.current = setTimeout(() => checkIdentifier(phone.trim()), 600);
    } else {
      setPhoneChecked(false); setName(""); setIsNewClient(false); setLoginInfo(null);
    }
    return () => { if (checkTimer.current) clearTimeout(checkTimer.current); };
  }, [phone]);

  const checkIdentifier = async (identifier) => {
    setCheckingPhone(true);
    try {
      const response = await axios.post(`${API}/auth/check-login`, { identifier });
      const data = response.data;
      setLoginInfo(data);
      setPhoneChecked(true);

      if (data.found && (data.type === "client" || data.type === "funcionario" || data.type === "global_client")) {
        setName(data.name || "");
        setIsNewClient(false);
      } else if (data.found && data.type === "user") {
        // Admin — precisa senha
        setName(data.name || "");
        setIsNewClient(false);
      } else {
        // Novo cliente
        setName("");
        setIsNewClient(true);
      }

      // Focar no campo nome
      setTimeout(() => nameRef.current?.focus(), 100);
    } catch {
      setPhoneChecked(true);
      setIsNewClient(true);
      setName("");
      setTimeout(() => nameRef.current?.focus(), 100);
    } finally {
      setCheckingPhone(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Admin — ir pra tela de senha
    if (loginInfo?.type === "user" && loginInfo?.needs_password) {
      setMode("password");
      return;
    }

    if (isPhoneInput && phoneDigits.length < 10) { toast.error("Digite um telefone valido"); return; }
    if (!isUsernameInput && !name.trim()) { toast.error("Digite seu nome"); return; }

    // Valida nome apenas quando vamos criar/atualizar o cliente (nao em login puro de admin/cliente local)
    const willSaveName = isNewClient || loginInfo?.type === "global_client" || !loginInfo?.found;
    let nameSanitized = name.trim();
    if (willSaveName) {
      const check = validateCustomerName(name);
      if (!check.valid) { toast.error(check.error); return; }
      nameSanitized = check.name;
    }

    setLoading(true);
    try {
      if (isNewClient || loginInfo?.type === "global_client" || !loginInfo?.found) {
        // Criar/atualizar global + login
        const phoneFormatted = ensureNinthDigit(phoneDigits);
        const response = await axios.post(`${API}/public/clientes`, {
          nome: nameSanitized,
          telefone: phoneFormatted
        });
        const newClient = response.data;

        // Se editou o nome e era global, atualizar
        if (loginInfo?.type === "global_client" && nameSanitized !== loginInfo?.name) {
          axios.post(`${API}/auth/update-global-name`, {
            phone: phoneFormatted, nome: nameSanitized
          }).catch(() => {});
        }

        const clientData = {
          id: newClient.id || null,
          nome: newClient.nome || nameSanitized,
          telefone: newClient.telefone || newClient.phone || phoneFormatted,
          phone: newClient.phone || phoneFormatted,
          global: newClient.global || !newClient.id,
        };
        localStorage.setItem("client", JSON.stringify(clientData));
        guardarPulseira(newClient.cliente_token);
        toast.success(isNewClient ? `Bem-vindo(a), ${clientData.nome}! 😊` : `Bem-vindo de volta, ${clientData.nome}! 😊`);
        onLoginSuccess("client", clientData);
        handleClose();
      } else if (loginInfo?.type === "client" || loginInfo?.type === "funcionario") {
        // Cliente local existente — login direto
        if (loginInfo.needs_password) {
          setMode("password");
        } else {
          await loginClient(loginInfo.client_id, null);
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "";
      toast.error(detail || "Erro ao entrar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const loginClient = async (clientId, senha) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/client-login`, {
        client_id: clientId, senha: senha || undefined,
        // A pulseira só sai com o telefone do cadastro (01/11/2026): o mesmo
        // que o check-login usou para achar o cliente.
        phone: phoneDigits || undefined,
      });
      const data = response.data;
      if (data.is_funcionario && data.access_token) {
        saveToken(data.access_token);
        localStorage.setItem("user", JSON.stringify(data.user));
        onLoginSuccess("admin", data.user);
      } else if (data.client) {
        localStorage.setItem("client", JSON.stringify(data.client));
        guardarPulseira(data.cliente_token);
        toast.success(`Bem-vindo, ${data.client.nome}! 😊`);
        onLoginSuccess("client", data.client);
      } else {
        toast.error(data.message || "Não foi possível entrar. Tente de novo.");
        return;
      }
      handleClose();
    } catch { toast.error("Erro no login"); }
    finally { setLoading(false); }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password) { toast.error("Digite a senha"); return; }
    setLoading(true);
    try {
      if (loginInfo?.type === "user") {
        const response = await axios.post(`${API}/auth/login`, { username: phone.trim(), password });
        const { access_token, user } = response.data;
        if (access_token) {
          saveToken(access_token);
          localStorage.setItem("user", JSON.stringify(user));
          onLoginSuccess("admin", user);
          handleClose();
        }
      } else {
        await loginClient(loginInfo.client_id, password);
      }
    } catch (error) {
      // Teste grátis vencido: o servidor recusa o login com 403 + motivo.
      const dados = error?.response?.data;
      if (error?.response?.status === 403 && dados?.motivo === "teste_vencido") {
        setAvisoTeste(dados.detail || "Seu teste grátis acabou.");
      } else {
        toast.error("Senha incorreta");
      }
    }
    finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={handleClose}>
      <div className="dark bg-zinc-900 text-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto shadow-2xl border border-zinc-800" onClick={e => e.stopPropagation()}>
        {/* Header com logo */}
        <div className="flex flex-col items-center pt-5 pb-3 border-b border-zinc-800 relative">
          <button onClick={handleClose} className="absolute top-3 right-3 p-2 hover:bg-zinc-800 rounded-full text-zinc-400"><X className="w-5 h-5" /></button>
          {logoUrl && (
            <img
              src={enderecoDaLogo(logoUrl)}
              alt="Logo"
              className="w-16 h-16 rounded-2xl object-contain mb-2"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <h2 className="font-bold text-lg text-white">{mode === "password" ? "Senha" : "Entrar"}</h2>
        </div>

        <div className="p-5">
          {/* ==================== TELA PRINCIPAL ==================== */}
          {mode === "phone" && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Telefone */}
              <div>
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-zinc-400" /> Telefone
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="phone" type="tel" inputMode="tel" value={phone}
                    onChange={e => {
                      const val = e.target.value;
                      if (/^\(?\d/.test(val) && !/[a-zA-Z]/.test(val)) {
                        setPhone(formatPhoneDisplay(val));
                      } else {
                        setPhone(val);
                      }
                    }}
                    placeholder="(00) 00000-0000" maxLength={16} autoFocus
                    className="text-lg py-6 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  />
                  {checkingPhone && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-zinc-400" />
                  )}
                </div>
                <p className="text-[11px] text-zinc-500 mt-1">Digite seu telefone com DDD</p>
              </div>

              {/* Nome — aparece somente apos digitar telefone completo */}
              <div className={`transition-all duration-300 overflow-hidden ${
                loginInfo?.type === 'user' ? 'hidden' :
                (isPhoneInput && phoneDigits.length >= 10 && phoneChecked) ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0 pointer-events-none'
              }`}>
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="w-4 h-4 text-zinc-400" /> Nome
                </Label>
                <Input
                  ref={nameRef}
                  id="name" value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder={isNewClient ? "Como quer ser chamado?" : "Seu nome"}
                  className="text-lg py-6 mt-1.5 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  disabled={phoneDigits.length < 10}
                />
                {phoneChecked && !isNewClient && loginInfo?.found && (
                  <p className="text-xs text-green-500 mt-1">Conta encontrada — edite o nome se precisar</p>
                )}
                {phoneChecked && isNewClient && (
                  <p className="text-xs text-zinc-500 mt-1">Primeira vez? Preencha seu nome</p>
                )}
              </div>

              {/* Botao */}
              <Button type="submit" disabled={loading || checkingPhone || (!isUsernameInput && phoneDigits.length < 10) || (!loginInfo?.type && !name.trim())}
                className="w-full py-6 bg-orange-500 hover:bg-orange-600 text-base">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  loginInfo?.type === "user" ? "Entrar" :
                  isNewClient ? "Criar conta e continuar" : "Continuar"
                )}
              </Button>
            </form>
          )}

          {/* ==================== TELA DE SENHA (ADMIN) ==================== */}
          {mode === "password" && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {avisoTeste && (
                <div role="alert" className="rounded-xl border border-orange-500/60 bg-orange-500/10 p-4 text-center">
                  <p className="text-sm font-semibold text-orange-300">{avisoTeste}</p>
                  <a
                    href={WHATSAPP_SUPORTE_PAGAMENTO}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-2 inline-block text-sm font-medium text-green-400 underline hover:text-green-300"
                  >
                    Fale com o suporte pelo WhatsApp para pagar
                  </a>
                </div>
              )}
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-orange-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Lock className="w-7 h-7 text-orange-500" />
                </div>
                <p className="font-semibold text-lg">{name || loginInfo?.name}</p>
                <p className="text-sm text-muted-foreground">Digite sua senha</p>
              </div>
              <div>
                <Label htmlFor="password">Senha</Label>
                <Input id="password" type="password" value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="Sua senha"
                  autoFocus className="text-lg py-6 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500" />
              </div>
              <Button type="submit" disabled={loading} className="w-full py-6 bg-orange-500 hover:bg-orange-600">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Entrar"}
              </Button>
              <button type="button" onClick={() => setMode("phone")}
                className="w-full text-sm text-muted-foreground hover:text-foreground">Voltar</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
